import { HTTPError } from 'ky';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { SettingPageHeader } from '../ui/setting.header';
import { SettingSectionLayout } from '../ui/setting.section';

import { USER_ROLE } from '@/entities/user/constants';
import { updateWorkspaceSettings } from '@/entities/workspace/api';
import type { WorkspaceSettingPayload } from '@/entities/workspace/types';
import { Button } from '@/shared/components/shadcn/button';
import { Input } from '@/shared/components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { useWorkspace, useWorkspaceCache } from '@/shared/hooks/use-workspace';

const initialValue = {
  name: '',
  language: 'en',
  timezone: 'America/Los_Angeles',
};

type FormData = WorkspaceSettingPayload;

export function GeneralPage() {
  const { workspace } = useWorkspace();
  const { invalidate } = useWorkspaceCache();
  const [loading, setLoading] = useState(false);

  const isOwner = workspace?.privilege === USER_ROLE.OWNER;

  const { register, handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      name: workspace?.name ?? initialValue.name,
      language: workspace?.language ?? initialValue.language,
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      await updateWorkspaceSettings(workspace.id, {
        name: data.name,
        language: data.language,
      });
      await invalidate();
      toast.success('Workspace settings updated');
    } catch (e) {
      if (e instanceof HTTPError) {
        if (e.response.status === 400) {
          const error = (await e.response.json()) as {
            code: number;
            message: string;
          };
          toast.error(error.message);
        }
      } else {
        console.error(e);
        toast.error('Failed to update workspace settings');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <SettingSectionLayout>
      <SettingPageHeader title="General" />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <span className="text-foreground text-sm font-medium">Workspace name</span>
            <Input
              {...register('name')}
              placeholder="Workspace name"
              className="h-10 w-full"
              disabled={!isOwner}
            />
            <p className="text-muted-foreground text-sm">Edit your workspace title here.</p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-foreground text-sm font-medium">Language Setting for AI</span>
            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={!isOwner}>
                  <SelectTrigger className="w-52">
                    <SelectValue placeholder="Select a language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ko">Korean</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />

            <p className="text-muted-foreground text-sm">
              AI outputs to be generated in the language selected below.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-foreground text-sm font-medium">Timezone</span>
            <Input
              value={workspace?.timezone || initialValue.timezone}
              placeholder="Select a timezone"
              className="h-10 w-52"
              disabled
            />
            {/* <Controller
              name="timezone"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-52">
                    <SelectValue placeholder="Select a timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    {timezonesList.map((timezone: TimeZone) => (
                      <SelectItem key={timezone.tzCode} value={timezone.tzCode}>
                        {timezone.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            /> */}
            <p className="text-muted-foreground text-sm">
              Default timezone is the local timezone of the admin when the user set up the
              workspace.
            </p>
          </div>
          <div className="flex">
            <Button type="submit" disabled={!isOwner || loading}>
              Update settings
            </Button>
          </div>
        </div>
      </form>
    </SettingSectionLayout>
  );
}
